import { useMemo } from 'react'
import { max, toNumber } from 'lodash-es'
import { roofStoreyKey, useModelStore } from '@editor/stores/modelStore'
import { sceneConfig } from '@modugen/ui-toolkit'
import useVisibleModel from './useVisibleModel'

type Assignment = Record<string, Partial<Record<ElementTypes, number>>>

const renderOrderLookup = {
  slabs: 0,
  roof_slabs: 0,
  vertical_slabs: 1,
  vertical_roof_slabs: 1,
  rips: 1,
  lintels: 1,
  slab_beams: 1,
  roof_slab_beams: 1,
  outer_walls: 2,
  inner_walls: 2,
  beams: 4,
  purlins: 4,
  columns: 5,
}

const RENDER_ORDER_DELTA_Z = 0.005
const ADDITIONAL_Z_TRANSLATION_FOR_TOP_STOREY_COLUMNS = 0.025

const useStoreyToElementTypeAndZTranslation = (storey: string | null | undefined) => {
  const visibleModel = useVisibleModel()

  const activeStoreyHeight = useMemo(() => {
    if (storey === undefined || storey === null) return undefined

    const boundary = visibleModel.storey_boundaries[storey]
    return boundary[1] - boundary[0] || 0
  }, [storey, visibleModel.storey_boundaries])
  const availableStoreys = useModelStore(state => state.availableStoreys)
  const maxAvailableNonRoofStorey = useMemo(
    () =>
      max(
        Array.from(availableStoreys)
          .filter(s => s !== roofStoreyKey)
          .map(s => toNumber(s)),
      )?.toString(),
    [availableStoreys],
  )
  const isTopStorey = useMemo(() => {
    return storey === maxAvailableNonRoofStorey
  }, [storey, maxAvailableNonRoofStorey])

  return useMemo<Assignment | undefined>(() => {
    if (storey === undefined || storey === null || activeStoreyHeight === undefined)
      return undefined

    const baseTranslation = 0
    const translationWithStoreyHeight = activeStoreyHeight

    const baseConfig: Partial<Record<ElementTypes, number>> = {
      inner_walls:
        translationWithStoreyHeight + renderOrderLookup.inner_walls * RENDER_ORDER_DELTA_Z,
      outer_walls:
        translationWithStoreyHeight + renderOrderLookup.outer_walls * RENDER_ORDER_DELTA_Z,
      beams: baseTranslation + renderOrderLookup.beams * RENDER_ORDER_DELTA_Z,
      columns: isTopStorey
        ? translationWithStoreyHeight + ADDITIONAL_Z_TRANSLATION_FOR_TOP_STOREY_COLUMNS
        : baseTranslation + renderOrderLookup.columns * RENDER_ORDER_DELTA_Z,
      rips: translationWithStoreyHeight + renderOrderLookup.rips * RENDER_ORDER_DELTA_Z,
      lintels: translationWithStoreyHeight + renderOrderLookup.lintels * RENDER_ORDER_DELTA_Z,
      slab_beams: translationWithStoreyHeight + renderOrderLookup.slab_beams * RENDER_ORDER_DELTA_Z,
      roof_slab_beams:
        translationWithStoreyHeight + renderOrderLookup.roof_slab_beams * RENDER_ORDER_DELTA_Z,
      slabs: baseTranslation + renderOrderLookup.slabs * RENDER_ORDER_DELTA_Z,
      roof_slabs: baseTranslation + renderOrderLookup.roof_slabs * RENDER_ORDER_DELTA_Z,
      vertical_slabs: baseTranslation + renderOrderLookup.vertical_slabs * RENDER_ORDER_DELTA_Z,
      vertical_roof_slabs:
        baseTranslation + renderOrderLookup.vertical_roof_slabs * RENDER_ORDER_DELTA_Z,
      purlins: translationWithStoreyHeight + renderOrderLookup.purlins * RENDER_ORDER_DELTA_Z,
    }

    return {
      [storey]: baseConfig,
      [sceneConfig.RoofStoreyKey]: baseConfig,
    }
  }, [storey, activeStoreyHeight, isTopStorey])
}

export default useStoreyToElementTypeAndZTranslation
